import { useSelector, useDispatch } from 'react-redux';
import { timeBucketActions, projectEntryActions, projectEntrySelectors, bulkEntryActions } from '@/store';
import React, { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import { handleFetchError, isTimeEntryDateValid, handleFetchSuccess } from '@/helpers';
import { SupportedSystems } from '@/shared';
import PageLoader from '../../components/PageLoader';
import { ProjectEntriesWrapper } from '../ProjectEntry/styled';
import {
  TimeBucketTimesheetContainer,
  HeaderRow,
  StyledArrowIcon,
  CategoryName
} from './styled';
import { errorSavingEntry, errorSavingEntryHours } from '../../shared/constants/toastrErrorMsg';
import { ProjectEntryRow } from '../../components/ProjectEntrySection/RowContent/styled';
import { Grid } from '@mantine/core';
import NewHourInput from '../../components/ProjectEntrySection/HoursInput/NewHourInput';
import FillWeekControl from '@/components/FillWeekControl';
import CopyPreviousButton from '../../components/CopyPreviousButton';

const shouldShowCalendarYearTotalLabel = (bucket) => {
  // Short circuit to always return false for now, as the requirement to show the calendar year total label is currently disabled.
  return false;
  // const name = bucket.name.toLowerCase();
  // return name === 'pto' || name === 'holiday';
};

const TimeBucketCategory = ({category}) => {
  const dispatch = useDispatch();
  const oid = useSelector(state => state.user.activeUser?.oid);
  const selectedDays = useSelector(state => state.projectEntry.projectEntries.selectedDays);
  const NumberOfPreviousTimeEntryMonthsAllowed = useSelector(state => state.config.configuration.NumberOfPreviousTimeEntryMonthsAllowed);
  const currentSystem = useSelector(projectEntrySelectors.selectCurrentSystem);
  const categoriesToBeRefreshed = useSelector(state => state.timeBucket.categoriesToBeRefreshed);
  const calendarYearTotalHours = useSelector(state => state.timeBucket.calendarYearTotalHours);

  const [isOpen, setIsOpen] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [entries, setEntries] = useState([]);
  const [copyPreviousLoading, setCopyPreviousLoading] = useState(false);
  const [copyPreviousWarningOpen, setCopyPreviousWarningOpen] = useState(false);

  const adminCategoryName = 'Administrator';
  const engineeringSystemName = 'VPIN';

  useEffect(() => {
    const loadCategoryEntries = async () => {
      if (!category) return;

      const data = await dispatch(
        timeBucketActions.getTimeBucketEntriesForCategory(oid, selectedDays, category.id)
      );
      setEntries(data);
      setIsLoading(false);
    };

    loadCategoryEntries();
  }, [category, category?.id, dispatch, oid, selectedDays]);

  useEffect(() => {
    const refreshCategoryEntries = async () => {
      if (!category?.id) return;
      if (!categoriesToBeRefreshed.includes(category.id)) return;

      const data = await dispatch(
        timeBucketActions.getTimeBucketEntriesForCategory(oid, selectedDays, category.id)
      );
      setEntries(data);
      dispatch(timeBucketActions.removeFromCategoriesToBeRefreshed([category.id]));
    };

    refreshCategoryEntries();
  }, [categoriesToBeRefreshed, category?.id, dispatch, oid, selectedDays]);

  useEffect(() => {
    const loadCalendarYearTotalHours = async () => {
      if (!category?.timeBuckets || !oid) return;

      // Load CYT hours for the PTO and Holiday buckets (matched by name).
      const cytBuckets = category.timeBuckets.filter(shouldShowCalendarYearTotalLabel);

      await Promise.all(
        cytBuckets.map(bucket =>
          dispatch(timeBucketActions.getTimeBucketCalendarYearTotal(oid, bucket.id))
        )
      );
    };

    loadCalendarYearTotalHours();
  }, [categoriesToBeRefreshed, category, category?.id, category?.timeBuckets, dispatch, oid]);

  const getErrMsg = (e) => {
    // If error has a Message property (from ValidationErrorResponse), use it directly
    if (e && e.Message) {
      return { title: 'Error: Project Entries', message: e.Message };
    }
    if (e.Message === errorSavingEntryHours.message) {
      return errorSavingEntryHours;
    }
    return errorSavingEntry;
  }
  
  
  const getTimeEntry = (bucket, date) => {
    if (!bucket) 
      return { hours: 0, entryDate: date, timeBucketCategoryId: category.id, timeBucketId: bucket.id  };
    
    const bucketEntries = entries.find(entry => entry.timeBucketId === bucket.id);
    if (!bucketEntries) 
      return { hours: 0, entryDate: date, timeBucketCategoryId: category.id, timeBucketId: bucket.id };
    
    const filteredList = bucketEntries.timeEntries.filter((x) => x);
    const timeEntry = filteredList.find(y => dayjs(y?.entryDate).format('MM/DD/YYYY 00:00:00') === dayjs(date).format('MM/DD/YYYY 00:00:00'))
      
    return {
      ...timeEntry,
      timeBucketCategoryId: bucketEntries.timeBucketCategoryId,
      timeBucketId: bucketEntries.timeBucketId,
      hours: timeEntry ? timeEntry.hours : 0
    };
  }

  const handleInputChange = async (change, finishedLoading, resetInput) => {
    if (change.previousValue == change.newValue) {
      finishedLoading();
      return;
    }
    const dateOnly = dayjs(change.date).format('YYYY-MM-DD');
    try {
      await dispatch(timeBucketActions.upsertTimeBucketTimeEntry(oid, dateOnly, change.entry.hours, change.entry.timeBucketId));
      
      var updateCompletedMonth = dayjs(dateOnly).month() === dayjs(selectedDays[0]).month();
      await dispatch(
        projectEntryActions.updateCompletedWeeks(change.newValue - change.previousValue, updateCompletedMonth)
      );
    } catch (e) {
      resetInput();
      handleFetchError(e, getErrMsg(e));
    }

    dispatch(timeBucketActions.updateCategoriesToBeRefreshed([category.id]));
    finishedLoading();
  }

  const handleCopyWeek = async (e) => {
    e.stopPropagation();
    setCopyPreviousLoading(true);
    try {
      const startDate = dayjs(selectedDays[0]).format('YYYY-MM-DD');
      const endDate = dayjs(selectedDays[6]).format('YYYY-MM-DD');
      const resp = await dispatch(bulkEntryActions.copyPreviousWeek(oid, startDate, endDate, 'Admin'));
      dispatch(timeBucketActions.updateCategoriesToBeRefreshed([category.id]));
      if(resp.item2 && resp.item2.length > 0) {
        let errorMessage= 'An error occurred while copying the previous week. \n';
        resp.item2.forEach(err => {        
          errorMessage += `${err.data.Id} - ${err.data.EntryDate}: ${err.message}\n`;
        });
        const errorInfo = {
          title: 'Error Copying Previous Week',
          message: errorMessage,
          timerOverride: 10000
        }
        handleFetchError(resp.item2, errorInfo);
      } else {
        handleFetchSuccess('Previous week copied successfully');
      }
    } catch (err) {
      handleFetchError(err, {
        title: 'Error Copying Previous Week',
        message: 'An unexpected error occurred while copying the previous week.'
      });
    } finally {
      setCopyPreviousLoading(false);
      setCopyPreviousWarningOpen(false);
    }
  }

  const handleFillWeekSuccess = async (data) => {
    setEntries(data);

    dispatch(timeBucketActions.updateCategoriesToBeRefreshed([category.id]));
  }

  const handleClickChange = (eventOrOpen) => {
    if (typeof eventOrOpen === 'boolean') {
      setCopyPreviousWarningOpen(eventOrOpen);
      return;
    }

    eventOrOpen.stopPropagation();
    setCopyPreviousWarningOpen(copyPreviousWarningOpen => !copyPreviousWarningOpen);
  }

  const getCalendarYearTotalHours = (bucketId) => {
    const cytData = calendarYearTotalHours[`timeBucketId_${bucketId}`];
    if (!cytData) return 0;
    return cytData.hours || 0;
  }

  return (
    <>
      <HeaderRow onClick={() => setIsOpen(!isOpen)}>
        <StyledArrowIcon icon="angle-right" isOpen={isOpen} />
        <CategoryName>{category.name}</CategoryName>
        {(category.name === adminCategoryName && currentSystem !== engineeringSystemName) 
          ? <CopyPreviousButton  copyPreviousLoading={copyPreviousLoading}
            copyPreviousWarningOpen={copyPreviousWarningOpen}
            onToggleWarning={handleClickChange}
            onCopyWeek={handleCopyWeek}
          />
          : null
        } 
      </HeaderRow>

      {isOpen && (
        <TimeBucketTimesheetContainer>
          { category.timeBuckets && !isLoading ? (
            <ProjectEntriesWrapper>
              {category.timeBuckets.map((bucket, index) => (
                <ProjectEntryRow key={bucket.id || index} align='center' active="true" style={{marginRight: '3px'}}>
                  <Grid.Col lg={8} md={7} xs={12}>
                    <Grid align="center">
                      <Grid.Col span={12} lg={5}>
                        <Grid>
                          <Grid.Col span={5}>
                            {bucket.name}
                          </Grid.Col>
                          <Grid.Col span={7}>
                            {shouldShowCalendarYearTotalLabel(bucket) && (
                              <span><strong>{getCalendarYearTotalHours(bucket.id)}</strong> hours used this year</span>
                            )}
                          </Grid.Col>
                        </Grid>
                      </Grid.Col>
                      <Grid.Col span={12} lg={5}>
                      </Grid.Col>
                      <Grid.Col span={12} lg={2}>
                        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                          <FillWeekControl
                            type={SupportedSystems.ADMIN}
                            projectData={{ timeBucketId: bucket.id, categoryId: category.id }}
                            onSuccess={handleFillWeekSuccess}
                          />
                        </div>
                      </Grid.Col>
                    </Grid>
                  </Grid.Col>
                  <Grid.Col lg={4} md={5} sm={8} xs={12}>
                    <Grid sm={{ justify: 'space-around'}} align="center">
                      {selectedDays.map((date) => {
                        const timeEntry = getTimeEntry(bucket, date)
                        const key = `entry-${index}-key-${dayjs(date).format('MM/DD/YYYY')}-${timeEntry.hours}`;
                        return <NewHourInput id={date} key={key} entry={timeEntry} editable={isTimeEntryDateValid(date, NumberOfPreviousTimeEntryMonthsAllowed)} date={dayjs(date).format('MM/DD/YYYY 00:00:00')} handleInputChange={handleInputChange} />
                      })}
                    </Grid>
                  </Grid.Col>
                </ProjectEntryRow>
              ))}
            </ProjectEntriesWrapper>
          ) : (
            <PageLoader />
          )}
        </TimeBucketTimesheetContainer>
      )}
    </>
  );
};

export default TimeBucketCategory;
